import { readStoredCompanyProfile } from "../../utils/companyProfileStorage"
import { buildBarcodeSvgMarkup } from "./ProductionOrders/barcode"

export const escapeHtml = (input: string) =>
  String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

export const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return "—"
  return value.toLocaleString()
}

export const getLabelStyles = () => `
  @page { size: 148mm 105mm; margin: 4mm; }
  body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0; color: #000; font-size: 10px; }
  .no-print { text-align: right; margin-bottom: 12px; }
  .label { border: 1px solid #000; padding: 5px; box-sizing: border-box; width: 140mm; height: 97mm; position: relative; margin: 0 auto 10px auto; page-break-after: always; }
  .label:last-child { page-break-after: avoid; }
  
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 5px; border-bottom: 1px solid #000; padding-bottom: 5px; }
  .logo-container { width: 40%; }
  .logo-placeholder { font-weight: bold; font-size: 18px; }
  .logo-img { max-height: 40px; }
  .company-info { width: 55%; text-align: right; font-size: 7px; line-height: 1.2; }
  
  .product-desc { text-align: center; font-size: 25px; margin: 5px 0; }
  .product-sku { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 5px; }
  
  .barcode-section { text-align: center; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 5px 0; }
  .barcode-svg { max-width: 100%; height: 60px; }
  .barcode-text { font-size: 9px; margin-top: 2px; letter-spacing: 2px; }
  
  .specs-table { width: 100%; border-collapse: collapse; margin-top: 5px; }
  .specs-table td { border: 1px solid #000; padding: 3px; vertical-align: top; width: 20%; }
  .spec-label { font-size: 7px; color: #333; display: block; }
  .spec-value { font-size: 11px; font-weight: bold; }
  
  .details-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; margin-top: 5px; font-size: 8px; }
  .details-col { padding-right: 5px; }
  .details-row { margin-bottom: 2px; }
  
  .bottom-info { position: absolute; bottom: 5px; left: 5px; right: 5px; font-size: 6px; color: #444; border-top: 0.5px solid #ccc; padding-top: 2px; }
  
  @media print { 
    .no-print { display: none; } 
    .label { border: 1px solid #000; border-radius: 0; } 
  }
`

export const getSimpleLabelStyles = () => `
  @page { size: 58mm auto; margin: 2mm; }
  body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0; color: #000; }
  .no-print { text-align: right; margin-bottom: 12px; }
  .simple-label {
    border: 1px solid #000;
    box-sizing: border-box;
    width: 54mm;
    min-height: 36mm;
    margin: 0 auto 4mm auto;
    padding: 2.5mm 2mm;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    page-break-after: always;
    text-align: center;
  }
  .simple-label:last-child { page-break-after: avoid; }
  .simple-product {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.15;
    margin-bottom: 2.5mm;
    max-width: 100%;
    word-break: break-word;
  }
  .simple-barcode { width: 100%; max-width: 48mm; }
  .simple-barcode svg { width: 100%; height: 18mm; }
  .simple-code { margin-top: 1.5mm; font-size: 9px; letter-spacing: 1px; font-weight: 600; }
  @media print { .no-print { display: none; } }
`

export type LabelData = {
  title: string
  productName: string
  sku: string
  barcodeValue: string
  specs: { label: string; value: string }[]
  detailsLeft: string[]
  detailsCenter: { label: string; value: string } | null
  detailsRight: string[]
  bottomText?: string
  secondaryTitle?: string
  companyProfile?: {
    shortName: string
    legalName: string
    taxId: string
    regon: string
    krs: string
    email: string
    phone: string
    website: string
    assets: {
      loginLogo: string
      labelLogo: string
    }
    alt: {
      loginLogo: string
      labelLogo: string
    }
    address: {
      line1: string
      line2: string
    }
  }
}

export const generateGenericLabelHtml = (data: LabelData) => {
  const companyProfile = data.companyProfile ?? readStoredCompanyProfile()
  const specsHtml = data.specs
    .map(
      (s) => `
      <td>
        <span class="spec-label">${escapeHtml(s.label)}</span>
        <span class="spec-value">${escapeHtml(s.value)}</span>
      </td>`,
    )
    .join("")

  const detailsLeftHtml = data.detailsLeft
    .map((d) => `<div class="details-row">${escapeHtml(d)}</div>`)
    .join("")

  const detailsRightHtml = data.detailsRight
    .map((d) => `<div class="details-row">${escapeHtml(d)}</div>`)
    .join("")

  const detailsCenterHtml = data.detailsCenter
    ? `
    <div class="details-col" style="text-align: center;">
      <span class="spec-label">${escapeHtml(data.detailsCenter.label)}</span>
      <span style="font-size: 24px; font-weight: bold;">${escapeHtml(
        data.detailsCenter.value,
      )}</span>
    </div>`
    : '<div class="details-col"></div>'

  const bottomText = data.bottomText || "&nbsp;"

  return `
    <section class="label">
      <div class="header">
        <div class="logo-container">
          <img src="${escapeHtml(companyProfile.assets.labelLogo)}" class="logo-img" alt="${escapeHtml(companyProfile.alt.labelLogo)}" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
          <div class="logo-placeholder" style="display:none;">${escapeHtml(companyProfile.shortName)}</div>
        </div>
        <div class="company-info">
          ${escapeHtml(companyProfile.legalName)}<br>
          ${escapeHtml(companyProfile.taxId)}<br>
          ${escapeHtml(companyProfile.regon)}<br>
          ${escapeHtml(companyProfile.krs)}<br>
          ${escapeHtml(companyProfile.address.line1)}<br>
          ${escapeHtml(companyProfile.address.line2)}<br>
          ${escapeHtml(companyProfile.phone)}<br>
          ${escapeHtml(companyProfile.email)} ${escapeHtml(companyProfile.website)}
        </div>
      </div>
      
      <div class="product-desc">${escapeHtml(data.productName)}</div>
      <div style="text-align: center; font-size: 8px; margin-bottom: 2px;">${escapeHtml(
        data.secondaryTitle || "",
      )}</div>
      
      <div class="barcode-section">
        <div class="barcode-svg">
          ${buildBarcodeSvgMarkup(data.barcodeValue, {
            height: 60,
            barWidth: 2,
            maxWidth: 550,
          })}
        </div>
        <div class="barcode-text">${escapeHtml(data.barcodeValue)}</div>
      </div>
      
      <table class="specs-table">
        <tr>
          ${specsHtml}
        </tr>
      </table>
      
      <div class="details-grid">
        <div class="details-col">
          ${detailsLeftHtml}
        </div>
        ${detailsCenterHtml}
        <div class="details-col" style="text-align: right;">
          ${detailsRightHtml}
        </div>
      </div>
      
      <div class="bottom-info">
        ${bottomText}
      </div>
    </section>`
}

export const generateSimpleLabelHtml = (data: {
  productName: string
  barcodeValue: string
}) => `
  <section class="simple-label">
    <div class="simple-product">${escapeHtml(data.productName || "—")}</div>
    <div class="simple-barcode">
      ${buildBarcodeSvgMarkup(data.barcodeValue || " ", {
        height: 70,
        barWidth: 2,
        maxWidth: 420,
      })}
    </div>
    <div class="simple-code">${escapeHtml(data.barcodeValue || "—")}</div>
  </section>
`
